import { apiRequest } from '@/utils/apiUtils'
import { User, PersonalProfileFormData, BankInfoFormData, PasswordChangeFormData, ThemeConfig, SystemSettings } from '@/types'

export const settingsService = {
  // 獲取用戶個人資料
  async getProfile(): Promise<User> {
    const response = await apiRequest<User>('/api/settings/profile', {
      method: 'GET'
    })
    return response.data
  },

  // 更新用戶個人資料
  async updateProfile(data: PersonalProfileFormData): Promise<User> {
    const response = await apiRequest<User>('/api/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return response.data
  },

  // 上傳頭像
  async uploadAvatar(file: File): Promise<User> {
    const formData = new FormData()
    formData.append('avatar', file)

    const response = await fetch('/api/settings/avatar', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '頭像上傳失敗')
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.message || '頭像上傳失敗')
    }

    return result.data
  },

  // 修改密碼
  async changePassword(data: PasswordChangeFormData): Promise<void> {
    await apiRequest('/api/settings/password', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  // 更新銀行資料
  async updateBankInfo(data: BankInfoFormData): Promise<User> {
    const response = await apiRequest<User>('/api/settings/bank-info', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return response.data
  },

  // 獲取主題設定
  async getThemeConfig(): Promise<ThemeConfig> {
    const response = await apiRequest<ThemeConfig>('/api/settings/theme', {
      method: 'GET'
    })
    return response.data
  },

  // 更新主題設定
  async updateThemeConfig(data: ThemeConfig): Promise<ThemeConfig> {
    const response = await apiRequest<ThemeConfig>('/api/settings/theme', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return response.data
  },

  // 獲取系統設定
  async getSystemSettings(): Promise<SystemSettings> {
    const response = await apiRequest<SystemSettings>('/api/settings/system', {
      method: 'GET'
    })
    return response.data
  },

  // 更新軟體名稱
  async updateSoftwareName(softwareName: string): Promise<{ softwareName: string }> {
    const response = await apiRequest<{ softwareName: string }>('/api/settings/system/software-name', {
      method: 'PUT',
      body: JSON.stringify({ softwareName })
    })
    return response.data
  },

  // 獲取軟體名稱（公開API，不需要身份驗證）
  async getPublicSoftwareName(): Promise<{ softwareName: string }> {
    try {
      const response = await fetch('/api/public/software-name', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error('無法獲取軟體名稱')
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.message || '獲取軟體名稱失敗')
      }

      return result.data
    } catch (error) {
      console.warn('獲取軟體名稱失敗，使用預設值:', error)
      return { softwareName: 'AnyTime' }
    }
  },

  // Webhook 相關 API
  
  // 獲取 Webhook 設定
  async getWebhookSettings(): Promise<any> {
    const response = await apiRequest('/api/settings/webhooks', {
      method: 'GET'
    })
    return response.data
  },

  // 更新 Webhook 事件開關
  async updateWebhookEvents(events: Record<string, boolean>): Promise<void> {
    await apiRequest('/api/settings/webhooks/events', {
      method: 'PUT',
      body: JSON.stringify({ events })
    })
  },

  // 更新預設 Webhook URL
  async updateDefaultWebhookUrl(url: string): Promise<void> {
    await apiRequest('/api/settings/webhooks/default-url', {
      method: 'PUT',
      body: JSON.stringify({ url })
    })
  },

  // 測試 Webhook
  async testWebhook(eventType?: string): Promise<void> {
    await apiRequest('/api/settings/webhooks/test', {
      method: 'POST',
      body: JSON.stringify(eventType ? { eventType } : {})
    })
  },

  // 創建 Webhook 配置
  async createWebhookConfig(config: { eventType: string; url: string; isEnabled: boolean }): Promise<any> {
    const response = await apiRequest('/api/settings/webhooks/configs', {
      method: 'POST',
      body: JSON.stringify(config)
    })
    return response.data
  },

  // 更新 Webhook 配置
  async updateWebhookConfig(id: string, config: { eventType?: string; url?: string; isEnabled?: boolean }): Promise<any> {
    const response = await apiRequest(`/api/settings/webhooks/configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(config)
    })
    return response.data
  },

  // 刪除 Webhook 配置
  async deleteWebhookConfig(id: string): Promise<void> {
    await apiRequest(`/api/settings/webhooks/configs/${id}`, {
      method: 'DELETE'
    })
  }
}
